import {
  Button,
  message,
  Input,
  DatePicker,
  Select,
  Space,
  Switch,
  Table,
} from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import {
  DownloadOutlined,
  SearchOutlined,
  DownOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { getUserRole } from "../../utils/auth";
import BalancePaidForm from "./components/BalancePaidForm";

// Extend Day.js with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

export default function RcInsurancePaymentReport() {
  const [payments, setPayments] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balanceFilter, setBalanceFilter] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    storeName: null,
    customerName: null,
    search: null,
    fromDate: null,
    toDate: null,
    financialYear: null,
  });
  const [userRole, setUserRole] = useState("");
  const [formVisible, setFormVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    setUserRole(getUserRole().role);
  }, []);

  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024;
    let endYear = startYear + 1;

    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears;
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value ? value : null,
    }));
  };

  const getPayments = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const {
        storeName,
        search,
        fromDate,
        toDate,
        customerName,
        financialYear,
      } = filters;
      const response = await API.get("/rc-insurance-payments", {
        params: {
          page,
          limit: pageSize,
          storeName,
          search,
          customerName,
          balance: balanceFilter ? true : undefined,
          fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
          toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          financialYear: financialYear || null,
        },
      });

      const paymentData = Array.isArray(response?.data?.payments)
        ? response.data.payments
        : [];
      // Log payments to debug balancePaid issues
      paymentData.forEach((payment, index) => {
        if (!Array.isArray(payment?.payment?.balancePaid)) {
          console.warn(
            `Invalid balancePaid for payment at index ${index}:`,
            payment
          );
        }
      });
      setPayments(paymentData);

      setPaginationInfo({
        current: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching RC insurance payments:", err);
      message.error("Failed to load RC insurance payments.");
    } finally {
      setLoading(false);
    }
  };

  const getStore = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();
      setUserRole(userRoleValue.role);
      if (userRoleValue.role !== "admin" && userStoreId) {
        const matchingStore = response.data.find(
          (store) => store._id === userStoreId
        );
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to load stores.");
    }
  };

  useEffect(() => {
    getStore();
    const data = localStorage.getItem("userData");
    const role = JSON.parse(data);
    setUserRole(role);
  }, []);

  useEffect(() => {
    getPayments(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize, balanceFilter]);

  const handleDownload = async (data) => {
    try {
      const result = await API.get(
        `/rc-insurance-payments/generateInvoice/${data?.saleId}`
      );
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `rc_insurance_invoice_${data?.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const handleOpenBalanceForm = (payment) => {
    setSelectedPayment(payment);
    setFormVisible(true);
  };

  const handleCloseBalanceForm = () => {
    setFormVisible(false);
    setSelectedPayment(null);
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        paginationInfo.total -
        ((paginationInfo.current - 1) * paginationInfo.pageSize + index),
      width: 80,
    },
    {
      title: "Invoice No.",
      render: (_, payment) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{payment?.invoiceNumber || "N/A"}</span>
          <span style={{ color: "gray", fontSize: "12px" }}>
            {payment.invoiceData?.invoiceId || "N/A"}
          </span>
        </div>
      ),
      key: "invoiceNumber",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange("search", e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
      width: 180,
    },
    {
      title: "Inv. Date",
      key: "orderDate",
      render: (_, payment) =>
        dayjs(payment?.invoiceData?.orderDate).format("DD-MM-YYYY"),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={
              selectedKeys && selectedKeys.length > 0
                ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])]
                : []
            }
            onChange={(dates) => {
              setSelectedKeys(
                dates
                  ? [
                      dates[0]?.format("YYYY-MM-DD"),
                      dates[1]?.format("YYYY-MM-DD"),
                    ]
                  : []
              );
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
      width: 120,
    },
    {
      title: "Particular",
      key: "particular",
      render: (_, payment) => payment?.payment?.particular || "RC Insurance",
      width: 150,
    },
    {
      title: "Chassis Number",
      key: "chassisNumber",
      render: (_, payment) => payment?.payment?.chassisNumber || "N/A",
      width: 150,
    },
    {
      title: "Total Amount (₹)",
      key: "totalAmount",
      render: (_, payment) =>
        `₹ ${new Intl.NumberFormat().format(
          payment?.payment?.totalAmount || 0
        )}`,
      width: 150,
    },
    {
      title: "Paid (₹)",
      key: "paidAmount",
      render: (_, payment) =>
        `₹ ${new Intl.NumberFormat().format(
          payment?.payment?.paidAmount || 0
        )}`,
      width: 100,
    },
    {
      title: "Balance (₹)",
      key: "balanceAmount",
      render: (_, payment) => {
        const total = payment?.payment?.balanceAmount || 0;
        // Defensive check for balancePaid
        const paid = Array.isArray(payment?.payment?.balancePaid)
          ? payment.payment.balancePaid.reduce(
              (sum, entry) => sum + (entry.amount || 0),
              0
            )
          : 0;
        const remaining = total - paid;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span style={{ color: remaining > 0 ? "red" : "green" }}>
              ₹ {new Intl.NumberFormat().format(total)}
            </span>
            {total > 0 && (
              <>
                <span>
                  <span style={{ color: "black" }}>Paid: </span>
                  <span style={{ color: remaining > 0 ? "red" : "green" }}>
                    ₹ {new Intl.NumberFormat().format(paid)}
                  </span>
                </span>
                <span>
                  <span style={{ color: "black" }}>Remaining: </span>
                  <span style={{ color: remaining > 0 ? "red" : "green" }}>
                    ₹ {new Intl.NumberFormat().format(remaining)}
                  </span>
                </span>
              </>
            )}
          </div>
        );
      },
      width: 160,
    },
    {
      title: "Pay Method",
      key: "paymentMethod",
      render: (_, payment) => {
        const { cashAmount, upiAmount } =
          payment?.payment?.paymentMethods || {};
        const methods = [];
        if (cashAmount > 0)
          methods.push(`CASH: ₹${new Intl.NumberFormat().format(cashAmount)}`);
        if (upiAmount > 0)
          methods.push(`UPI: ₹${new Intl.NumberFormat().format(upiAmount)}`);
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {methods.length > 0 ? (
              methods.map((method, index) => <span key={index}>{method}</span>)
            ) : (
              <span>--</span>
            )}
          </div>
        );
      },
      width: 120,
    },
    {
      title: "Customer",
      key: "customerName",
      render: (_, payment) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {payment?.customerId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {payment?.customerId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleSearchChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
      width: 200,
    },
    {
      title: "Store",
      key: "storeName",
      render: (_, payment) => (
        <div>
          <div
            style={{ fontWeight: "bold", color: "#1591EA", fontSize: "12px" }}
          >
            {payment?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
      width: 220,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") {
          color = "yellow";
        } else if (status === "completed") {
          color = "green";
        } else if (status === "cancelled") {
          color = "red";
        }
        return (
          <span style={{ color: color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, payment) => (
        <div>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(payment)}
            style={{ marginRight: "8px", color: "green" }}
          />
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={() => handleOpenBalanceForm(payment)}
            disabled={payment?.payment?.balanceAmount <= 0}
          />
        </div>
      ),
      width: 120,
    },
  ];

  // Expandable row to show balancePaid details
  const expandedRowRender = (payment) => {
    const balancePaid = Array.isArray(payment?.payment?.balancePaid)
      ? payment.payment.balancePaid
      : [];
    const columns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (date) => dayjs(date).format("DD-MM-YYYY"),
      },
      {
        title: "Amount (₹)",
        dataIndex: "amount",
        key: "amount",
        render: (amount) => `₹ ${new Intl.NumberFormat().format(amount)}`,
      },
      {
        title: "Cash (₹)",
        dataIndex: "cash",
        key: "cash",
        render: (cash) => `₹ ${new Intl.NumberFormat().format(cash)}`,
      },
      {
        title: "UPI (₹)",
        dataIndex: "upi",
        key: "upi",
        render: (upi) => `₹ ${new Intl.NumberFormat().format(upi)}`,
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={balancePaid}
        pagination={false}
        rowKey={(record) => record.date + record.amount}
      />
    );
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          fontSize: "20px",
          textAlign: "center",
          marginBottom: "10px",
          color: "#1890ff",
        }}
      >
        RC Insurance Payment Report
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "10px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "500" }}>
          Filter by Store:
        </div>
        <Space>
          <Switch
            checked={balanceFilter}
            onChange={(checked) => {
              setBalanceFilter(checked);
            }}
          />
          <Select
            style={{ width: 150 }}
            placeholder="Store"
            value={filters.storeName}
            onChange={(value) => handleSearchChange("storeName", value)}
            allowClear
            disabled={userRole !== "admin"}
          >
            {stores.map((store) => (
              <Select.Option key={store._id} value={store.name}>
                {store.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder="Select Financial Year"
            value={filters.financialYear}
            onChange={(value) => handleSearchChange("financialYear", value)}
            allowClear
          >
            {generateFinancialYears().map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Table
          columns={columns}
          dataSource={payments}
          scroll={{ x: 1930 }}
          loading={loading}
          style={{ padding: "1px", width: "100%" }}
          pagination={{
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            onChange: (page, pageSize) => {
              setPaginationInfo({ current: page, pageSize });
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          rowKey={(record) => record._id}
          expandable={{
            expandedRowRender,
            expandIcon: ({ expanded, onExpand, record }) => (
              <DownOutlined
                onClick={(e) => onExpand(record, e)}
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  cursor: "pointer",
                }}
              />
            ),
            rowExpandable: (record) =>
              Array.isArray(record.payment?.balancePaid) &&
              record.payment.balancePaid.length > 0,
          }}
        />
      </div>

      <BalancePaidForm
        visible={formVisible}
        onCancel={handleCloseBalanceForm}
        payment={selectedPayment}
        onSuccess={() =>
          getPayments(paginationInfo.current, paginationInfo.pageSize, filters)
        }
      />
    </div>
  );
}
